import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import route from '@src/constants/route';
import actions from '@src/redux/actions';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import createPoller from '@src/utils/poller';
import apis from '@src/apis';
import { EXTRACTION_STATUS } from '@src/constants/project';

const useTranscriptPolling = ({ projectId, isActive, onComplete }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { user } = useSelector((state) => state.auth);
  const { getFeatureValue } = useFeatureFlags();
  const abortControllerRef = useRef(null);

  const transcriptAudioEnabled = getFeatureValue(
    FEATURE_KEYS.TRANSCRIPT_AUDIO,
    {
      userId: user?.id,
      email: user?.email,
      phoneNumber: user?.phoneNumber,
    },
  );

  useEffect(() => {
    if (!isActive || !transcriptAudioEnabled) return undefined;
    const pollTranscriptProgress = (id, signal) =>
      createPoller(() => apis.project.getSourcePreparationProgress(id), {
        isSuccess: (r) => r?.result?.status === EXTRACTION_STATUS.SUCCESS,
        isFailure: (r) => r?.result?.status === EXTRACTION_STATUS.FAILURE,
        signal,
        failureError: 'transcription-failed',
      });

    const controller = new AbortController();
    abortControllerRef.current = controller;

    const runPolling = async () => {
      try {
        await pollTranscriptProgress(projectId, controller.signal);
        onComplete();
      } catch (err) {
        if (err.name === 'AbortError') return;
        dispatch(actions.project.setIsLoadingProjectDetail(false));
        dispatch(
          actions.noti.push({
            severity: 'error',
            message: 'createProjectFailed',
          }),
        );
        history.replace(route.PROJECTS);
      }
    };

    runPolling();

    return () => {
      controller.abort();
      abortControllerRef.current = null;
    };
  }, [
    isActive,
    transcriptAudioEnabled,
    projectId,
    onComplete,
    dispatch,
    history,
  ]);
};

export default useTranscriptPolling;
